import { Code2, Layers, Database, Wrench, Sparkles, Gamepad2, Globe, Puzzle, Radio } from 'lucide-react';
import { SiHtml5, SiCss3, SiJavascript, SiPython, SiNextdotjs, SiExpress, SiFlask, SiBootstrap, SiTailwindcss, SiRedux, SiRecoil, SiMongodb, SiMysql, SiFirebase, SiPostman, SiSupabase } from 'react-icons/si';
import { FaJava, FaReact, FaAws, FaNodeJs, FaGitAlt, FaGithub, FaFigma } from 'react-icons/fa';

const CATEGORIES = [
  {
    key: 'languages',
    title: 'Languages',
    icon: Code2,
    tint: 'bg-violet-50/60 border-violet-100',
    span: 'lg:col-span-5',
    skills: [
      { label: 'HTML5', icon: SiHtml5, color: '#E34F26' },
      { label: 'CSS3', icon: SiCss3, color: '#1572B6' },
      { label: 'JavaScript', icon: SiJavascript, color: '#B7A100' },
      { label: 'Python', icon: SiPython, color: '#3776AB' },
      { label: 'Core Java', icon: FaJava, color: '#5382A1' },
    ],
  },
  {
    key: 'frameworks',
    title: 'Libraries & Frameworks',
    icon: Layers,
    tint: 'bg-pink-50/60 border-pink-100',
    span: 'lg:col-span-7',
    skills: [
      { label: 'React.js', icon: FaReact, color: '#149ECA' },
      { label: 'Next.js', icon: SiNextdotjs, color: '#000000' },
      { label: 'Express.js', icon: SiExpress, color: '#000000' },
      { label: 'Flask', icon: SiFlask, color: '#000000' },
      { label: 'Bootstrap', icon: SiBootstrap, color: '#7952B3' },
      { label: 'Tailwind CSS', icon: SiTailwindcss, color: '#06B6D4' },
      { label: 'Redux', icon: SiRedux, color: '#764ABC' },
      { label: 'Recoil', icon: SiRecoil, color: '#3578E5' },
      { label: 'WebSocket', icon: Radio, color: '#7C3AED' },
    ],
  },
  {
    key: 'databases',
    title: 'Databases',
    icon: Database,
    tint: 'bg-slate-50 border-slate-200',
    span: 'lg:col-span-4',
    skills: [
      { label: 'MongoDB', icon: SiMongodb, color: '#47A248' },
      { label: 'MySQL', icon: SiMysql, color: '#4479A1' },
      { label: 'Firebase', icon: SiFirebase, color: '#DD9722' },
      { label: 'Supabase', icon: SiSupabase, color: '#3ECF8E' },
      { label: 'AWS', icon: FaAws, color: '#FF9900' },
    ],
  },
  {
    key: 'tools',
    title: 'Tools',
    icon: Wrench,
    tint: 'bg-amber-50/60 border-amber-100',
    span: 'lg:col-span-4',
    skills: [
      { label: 'Node.js', icon: FaNodeJs, color: '#339933' },
      { label: 'Git', icon: FaGitAlt, color: '#F05032' },
      { label: 'GitHub', icon: FaGithub, color: '#181717' },
      { label: 'Postman', icon: SiPostman, color: '#FF6C37' },
      { label: 'VS Code', icon: Code2, color: '#7C3AED' },
      { label: 'Figma', icon: FaFigma, color: '#F24E1E' },
    ],
  },
  {
    key: 'specializations',
    title: 'Specializations',
    icon: Sparkles,
    tint: 'bg-gradient-to-br from-violet-50 to-pink-50 border-violet-100',
    span: 'lg:col-span-4',
    skills: [
      { label: 'Game Development', icon: Gamepad2, color: '#7C3AED' },
      { label: 'Full Stack Development', icon: Layers, color: '#7C3AED' },
      { label: 'Web Development', icon: Globe, color: '#7C3AED' },
      { label: 'Gen AI', icon: Sparkles, color: '#7C3AED' },
      { label: 'Problem Solving', icon: Puzzle, color: '#7C3AED' },
    ],
  },
];

export default function Skills() {
  return (
    <section id="skills" className="py-24 px-6 lg:px-8 bg-slate-50/60">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-slate-900 mb-4">
          Technical Skills
        </h2>
        <p className="text-slate-500 text-lg max-w-2xl mb-14">
          Technologies I reach for, organized the way I actually use them.
        </p>

        <div className="grid lg:grid-cols-12 gap-6">
          {CATEGORIES.map((cat) => (
            <div
              key={cat.key}
              className={`rounded-2xl border p-6 sm:p-7 ${cat.tint} ${cat.span}`}
            >
              <div className="flex items-center gap-2.5 mb-5">
                <cat.icon size={19} className="text-slate-700" />
                <h3 className="font-display font-semibold text-lg text-slate-800">
                  {cat.title}
                </h3>
              </div>
              <div className="flex flex-wrap gap-2.5">
                {cat.skills.map((skill) => (
                  <div
                    key={skill.label}
                    className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-slate-200/80 shadow-sm shadow-slate-200/50 text-sm font-medium text-slate-700 hover:-translate-y-0.5 hover:shadow-md transition-all duration-200"
                  >
                    <skill.icon size={16} style={{ color: skill.color }} />
                    {skill.label}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}